import { useState, useEffect, useRef, useCallback } from 'react';

export function useScrollIndicators() {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const [canScrollUp, setCanScrollUp] = useState(false);

    const checkScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;

        // Check if we can scroll up (with a small buffer)
        setCanScrollUp(el.scrollTop > 5);

        // Check if we can scroll down (with a small buffer)
        const isAtBottom = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) < 10;
        const isScrollable = el.scrollHeight > el.clientHeight + 5;
        setCanScrollDown(!isAtBottom && isScrollable);
    }, []);

    // Initial scroll check and ResizeObserver for height changes
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        el.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);

        // Also watch for content changes within the scroll area
        const observer = new ResizeObserver(() => checkScroll());
        observer.observe(el);

        // Multiple checks to ensure we catch the layout after animations/markdown rendering
        const timeouts = [100, 500, 1000, 2000].map(delay =>
            setTimeout(checkScroll, delay)
        );

        // Initial check
        checkScroll();

        return () => {
            el.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
            observer.disconnect();
            timeouts.forEach(clearTimeout);
        };
    }, [checkScroll]);

    return {
        scrollRef,
        canScrollUp,
        canScrollDown,
        checkScroll
    };
}
